import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { LocalBundleEnvelopeWithChosenMainVariant } from '@cdaas/reusable-components-daas/dist/src/product-api-types/api-types';
import { Delivery } from '@cdaas/reusable-components-daas/dist/src/asset-api-types/api-types';
import { DeliveryMethodOfOrder } from '@cdaas/reusable-components-daas/dist/src/order-api-types/api-types';
import { OrganizationWithUserGroupsResponse, UserResponse } from '@cdaas/api-clients/dist/asset-admin-openapi';
import { SalesChannel } from '@cdaas/api-clients/dist/product-service-openapi';
import { ProductOrder } from '@cdaas/api-clients/dist/order-openapi';

import { RootState } from '.';
import { UserResponseWithUserGroup } from '../utils';

interface OrderState {
  organization: OrganizationWithUserGroupsResponse | null;
  salesChannel: SalesChannel | null;
  selectedUser: UserResponseWithUserGroup | null;
  selectedProduct: LocalBundleEnvelopeWithChosenMainVariant | null;
  deliveryDetails: Delivery | null;
  deliveryOption: DeliveryMethodOfOrder | null;
  completedOrder: ProductOrder | null;
}

export const initialUiState: OrderState = {
  organization: null,
  salesChannel: null,
  selectedUser: null,
  selectedProduct: null,
  deliveryDetails: null,
  deliveryOption: null,
  completedOrder: null,
};

const order = createSlice({
  name: 'order',
  initialState: { ...initialUiState },
  reducers: {
    setOrderOrganization: (state, action: PayloadAction<OrganizationWithUserGroupsResponse>) => {
      state.organization = action.payload;
    },
    setOrderSalesChannel: (state, action: PayloadAction<SalesChannel>) => {
      state.salesChannel = action.payload;
    },
    setOrderUser: (state, action: PayloadAction<UserResponse>) => {
      state.selectedUser = action.payload;
      state.selectedProduct = null;
      state.deliveryDetails = null;
      state.deliveryOption = null;
      state.completedOrder = null;
    },
    setOrderProduct: (state, action: PayloadAction<LocalBundleEnvelopeWithChosenMainVariant>) => {
      state.selectedProduct = action.payload;
    },
    resetOrderState: () => {
      return { ...initialUiState };
    },
    resetLocationReference: state => {
      if (state.deliveryDetails) {
        state.deliveryDetails.locationReference = null;
      }
    },
    setOrderDeliveryDetails: (state, action: PayloadAction<Delivery>) => {
      state.deliveryDetails = action.payload;
    },
    setOrderDeliveryOption: (state, action: PayloadAction<DeliveryMethodOfOrder>) => {
      state.deliveryOption = action.payload;
    },
    setCompletedOrder: (state, action: PayloadAction<ProductOrder>) => {
      state.completedOrder = action.payload;
    },
  },
});

export const {
  setOrderOrganization,
  setOrderSalesChannel,
  setOrderUser,
  setOrderProduct,
  resetOrderState,
  resetLocationReference,
  setOrderDeliveryDetails,
  setOrderDeliveryOption,
  setCompletedOrder,
} = order.actions;

export default order.reducer;

export const selectOrganization = (state: RootState) => state.order.organization;
export const selectSalesChannel = (state: RootState) => state.order.salesChannel;
export const selectSelectedUser = (state: RootState) => state.order.selectedUser;
export const selectSelectedProduct = (state: RootState) => state.order.selectedProduct;
export const selectDeliveryDetails = (state: RootState) => state.order.deliveryDetails;
export const selectDeliveryOption = (state: RootState) => state.order.deliveryOption;
export const selectCompletedOrder = (state: RootState) => state.order.completedOrder;
